<template>
  <plants-table
    :company="company"
    :paginated-response="paginatedResponse"
    :loading="loadingPlants"
    :shown-fields="shownFields"
    :sort-by="sortBy"
    :sort-order="sortOrder"
    :current-page="page"
    @row-opened="onRowClicked"
    @page-changed="handlePageChange"
    @sort-changed="handleSortChanged"
    @fetch-required="handleFetchRequired"
  />
</template>

<script lang="ts">
import _ from 'lodash';
import type { PropType } from 'vue';
import Company from '@/domain/model/business/Company';
import { makeCancelable } from '@/infrastructure/support/PromiseCancelable.ts';
import RouterHelper from '@/presentation/mixins/RouterHelper.js';
import type ResourceType from '@/presentation/pages/plants/widgets/ResourceType.ts';
import PaginatedResponse from '@/presentation/pages/resources/PaginatedResponse.ts';
import PlantsTable from '@/presentation/pages/resources/PlantsTable.vue';
import TweakParamsForPlantCall from '@/presentation/pages/resources/TweakParamsForPlantCall';

export default {
  name: 'PlantsOverviewTable',
  components: { PlantsTable },
  mixins: [RouterHelper, TweakParamsForPlantCall],
  inheritAttrs: false,
  emits: ['total-items', 'row-opened'],
  props: {
    resourceType: { type: Number as PropType<ResourceType> },
    company: { type: Company },
    filters: { type: Object },
    desiredTableFields: { type: Array, required: true },
    startSortedBy: { type: String, default: () => null },
    startSortOrder: { type: Number, default: () => null },
    createPromiseForPaginatedResponse: { type: Function, required: true },
    rowClicked: { type: Function },
  },
  data() {
    return {
      paginatedResponse: new PaginatedResponse([], 0, 15),

      loadingPlants: true,
      ongoingPromise: null,

      page: 1,
      sortBy: null,
      sortOrder: null,
      shownFields: [],

      // when the filter is opened, then the matching field is added as a column
      filterFieldMap: new Map<string, string>([
        ['tag_ids', 'tags'],
        ['ltpm', 'current_promise_status'],
        ['driver_account_ids', 'driver_accounts'],
        ['child_company_ids', 'attached_companies'],
        // one-on-ones
        ['city', 'city'],
        ['street', 'street'],
        ['street_number', 'street_number'],
        ['plant_code', 'plant_code'],
        ['peak_in_watt', 'peak_in_watt'],
        ['custom_fields', 'custom_fields'],
        ['issues', 'issues'],
        ['plant_code', 'plant_code'],
        ['tickets', 'tickets'],
        ['postal_code', 'postal_code'],
        ['monitored_since', 'monitored_since'],
        ['yield_score_7d', 'yield_score_7d'],
        ['yield_score_30d', 'yield_score_30d'],
      ]),
      // when the fields shown, then these relations should be requested
      fieldRelaMap: new Map<string, string[]>([
        ['issues', ['meter_issues']],
        ['current_promise_status', ['promise_statuses']],
        ['status_since', ['promise_statuses.last_change']],
        ['found_on', ['meter_issues', 'issue']],
        ['driver_accounts', ['meters.driver_account']],
        ['attached_companies', ['companies']],
        ['snoozed_until', ['issue']],
        ['new_issue', ['issue']],
        ['existing_issues', ['issue']],
        ['peak_in_watt', ['meters']],
        // one-on-ones
        ['tags', ['tags']],
        ['yield_score_7d', ['yield_score_7d']],
        ['yield_score_30d', ['yield_score_30d']],
        ['custom_fields', ['custom_fields']],
        ['tickets', ['tickets']],
        ['city', ['address']],
        ['street', ['address']],
        ['street_number', ['address']],
        ['postal_code', ['address']],
      ]),
    };
  },
  created() {
    this.sortBy = this.startSortedBy;
    this.sortOrder = this.startSortOrder;
  },
  mounted() {
    // filters should be null on cold-start, but notNull when re-mounting; e.g. tabs
    if (this.filters) {
      this.fillPlants();
    }
  },
  watch: {
    filters() {
      this.page = 1;
      this.fillPlants();
    },
  },
  methods: {
    updateShownFields() {
      const result = new Set(this.desiredTableFields);

      Object.keys(this.filters)
        .filter((key) => this.filterFieldMap.has(key))
        .map((key) => this.filterFieldMap.get(key))
        .forEach((field) => result.add(field));

      this.shownFields = [...result];
    },
    onRowClicked(rowData, rowIndex, mouseEvent) {
      this.rowClicked
        ? this.rowClicked(rowData, rowIndex, mouseEvent)
        : this.onRowClickedDefault(rowData, rowIndex, mouseEvent);
    },
    onRowClickedDefault(rowData, rowIndex, mouseEvent) {
      const routeDescription = {
        name: 'SinglePlant',
        params: { plantId: parseInt(rowData.plant.id) },
      };
      // needed for e.g. searchCart
      this.$emit('row-opened');

      this.resolveAndFollowWMouseEvent(
        this.$router,
        routeDescription,
        mouseEvent,
      );
    },

    // NEW
    cancelAnyOngoingPromise() {
      if (this.ongoingPromise) {
        this.ongoingPromise.cancel();
        this.ongoingPromise = null;
      }
    },
    handleSortChanged(sortChangedEvent) {
      this.sortBy = sortChangedEvent.sortField;
      this.sortOrder = sortChangedEvent.sortOrder;
      this.page = 1;

      this.fillPlants();
    },
    handlePageChange(newPage) {
      this.page = newPage;
      this.fillPlants();
    },
    handleFetchRequired() {
      this.fillPlants();
    },
    // FP used via ref
    handleUpdateComing() {
      this.loadingPlants = true;
      this.$emit('total-items', null);
      this.cancelAnyOngoingPromise();
    },
    fillPlants() {
      this.loadingPlants = true;

      this.updateShownFields();
      this.$emit('total-items', null);

      const promiseForItems = this.createPromiseForPaginatedResponse(
        this.makeParamsForApiCall(),
        this.shownFields,
      );

      this.cancelAnyOngoingPromise();
      this.ongoingPromise = makeCancelable(promiseForItems);
      this.ongoingPromise.then((paginatedResponse: PaginatedResponse) => {
        this.paginatedResponse = paginatedResponse;
        this.$emit('total-items', paginatedResponse.totalItems);
        this.loadingPlants = false;
      });
      this.ongoingPromise.catch(() => {
        // .finally() gets called upon cancelling, so do this here;
        this.loadingPlants = false;
      });
    },
    makeParamsForApiCall() {
      const filtersWoNulls = Object.keys(this.filters)
        .filter((fieldName) => this.filters[fieldName] !== null)
        .reduce((res, field) => ((res[field] = this.filters[field]), res), {});

      return this.tweakParamsForPlantsCall(
        Object.assign(
          { page: this.page, fields: _.join(this.shownFields, ',') },
          filtersWoNulls,
        ),
      );
    },
  },
};
</script>
